import { FC, useEffect, useState } from "react";
import { Button } from '../../../components/Button';
import SceneItem from "./SceneItem/SceneItem";
import {
  useCreateSceneMutation,
  useGetStudioQuery,
  useChangeSceneStudioMutation,
  User
} from "../../../features/api/apiSlice";
import styles from "./Scene.module.css";
import cn from "classnames";
import { useNavigate } from "react-router-dom";

interface SceneProps {
  user?: User;
}

const Scene: FC<SceneProps> = ({ user }) => {
  const { data: studio, error: studioError, isLoading, refetch } = useGetStudioQuery();
  const [createScene] = useCreateSceneMutation();
  const [changeScene, { isLoading: isChangingScene }] = useChangeSceneStudioMutation();
  const navigate = useNavigate();

  const [pendingSceneId, setPendingSceneId] = useState<number | null>(null);
  const [transitionActive, setTransitionActive] = useState(false);

  const currentSceneId = studio?.current_scene?.id || null;

  const scenesCount = studio?.studio_scenes?.length || 0;
  const isMaxScenesReached = scenesCount >= 5;

  useEffect(() => {
    if (!isChangingScene && pendingSceneId) {
      refetch();

      setTransitionActive(false);
      setPendingSceneId(null);
    }
  }, [isChangingScene, pendingSceneId, refetch]);

  const hasStudioAccess = (): boolean => {
    if (!user?.tariff?.studio) {
      navigate('/tariffs');
      return false;
    }
    return true;
  };

  const handleSceneChange = (sceneId: number) => {
    if (sceneId === currentSceneId || transitionActive || !hasStudioAccess()) {
      return;
    }

    setTransitionActive(true);
    setPendingSceneId(sceneId);

    changeScene({ sceneId });
  };

  const handleCreateScene = () => {
    if (isMaxScenesReached || !hasStudioAccess()) {
      return;
    }

    createScene()
      .then(() => refetch())
      .catch(error => console.error('Failed to create scene:', error));
  };

  if (isLoading) {
    return <div className={styles.scene__message}>Загрузка сцен...</div>;
  }

  if (studioError) {
    return <div className={cn(styles.scene__message, styles['scene__message--error'])}>
      Ошибка при загрузке данных
    </div>;
  }

  if (!studio) {
    return <div className={cn(styles.scene__message, styles['scene__message--error'])}>
      Данные студии не найдены
    </div>;
  }

  return (
    <div className={styles.scene__background}>
      <div className={styles["scene__button-group"]}>
        <Button
          className={styles['scene__add-button']}
          kind="primary"
          onClick={handleCreateScene}
          disabled={transitionActive || isMaxScenesReached}
        >
          Создать сцену
        </Button>
      </div>

      <div className={styles.scene}>
        <div className={styles.scene__samples}>
          {!studio.studio_scenes?.length ? (
            <div className={styles.scene__message}>Нет доступных сцен</div>
          ) : (
            studio.studio_scenes.map((scene) => (
              <SceneItem
                key={scene.id}
                {...scene}
                isActive={currentSceneId === scene.id}
                isPending={pendingSceneId === scene.id}
                isTransitioning={transitionActive}
                onSceneSelect={handleSceneChange}
                canDelete={studio.studio_scenes.length > 1}
                isShowEdit={user?.tariff.studio}
                studio={studio}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Scene;